import {
  ArrayOfOneOrMore,
  FundingPool,
  GeneralResourceDetail,
  PracticeDefinition,
  ProgramEngagementModel,
} from '@cibo/core'
import { FieldNameCell, useUpdateMultipleFields } from '@cibo/landmanager'
import { NotFound, ResourceDetailFeatureTaskEditorProps, SkeletonTable } from '@cibo/ui'
import {
  Box,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { TFunction } from 'i18next'
import { uniqBy } from 'ramda'
import { useTranslation } from 'react-i18next'
import { MultiselectChangeAllInput } from '../../../features/ResourceDetailFeatures/EQIP/MultiselectCheckboxFieldTable/MultiselectChangeAllInput'
import {
  MultiselectCheckboxProvider,
  useMultiselectCheckboxContext,
} from '../../../features/ResourceDetailFeatures/EQIP/MultiselectCheckboxFieldTable/MultiselectCheckboxContext'
import { MultiselectFieldInput } from '../../../features/ResourceDetailFeatures/EQIP/MultiselectCheckboxFieldTable/MultiselectFieldInput'
import { RECOMMENDED_PRACTICES_QUERY_KEY, useRecommendedFieldPractices } from '../../../hooks'
import { useEngagementTaskData } from '../../../queries/useEngagementTaskData'
import { useOptionsDisplay } from './useOptionsDisplay'

export type FieldsSelectionV2Props<T extends GeneralResourceDetail = GeneralResourceDetail> =
  ResourceDetailFeatureTaskEditorProps<T> & {
    engagement?: ProgramEngagementModel
  }

const displayString =
  (t: TFunction) =>
  (
    selected: ArrayOfOneOrMore<string>,
    optionDisplayString: (o: string) => string | undefined
  ): string => {
    const optionDisplaySelected = selected.map(optionDisplayString).filter(Boolean) as string[]
    const allOptionsDisplay = optionDisplaySelected.join(', ')
    if (selected.length === 0 || selected[0] === 'none') {
      return t('selectPractices')
    } else if (allOptionsDisplay.length > 40) {
      const displayString = `${optionDisplaySelected.slice(0, 1)}, ${t('andCountMore', {
        count: selected.length - 1,
      })}`
      if (displayString.length > 40) {
        if (selected.length === 1) {
          return allOptionsDisplay
        }
        return t('countSelectedPractices', { count: selected.length })
      } else {
        return displayString
      }
    } else {
      return allOptionsDisplay
    }
  }

const FieldsSelectionV2Display = <T extends GeneralResourceDetail = GeneralResourceDetail>({
  engagementId,
  userRole,
  ownerName,
}: FieldsSelectionV2Props<T>) => {
  const { t } = useTranslation('@cibo/programs/FieldsSelection')
  const recommendedPractices = useRecommendedFieldPractices(engagementId)
  const optionsOverride = useOptionsDisplay({ engagementId })
  const { fieldAnswers, options, programEngagementFields } = useMultiselectCheckboxContext()

  return (
    <Card variant="outlined">
      <Box p={2}>
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            <Typography variant="h6" component="h2">
              {t('title', {
                context: userRole,
                name: ownerName,
              })}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('subtitle', {
                context: userRole,
                name: ownerName,
              })}
            </Typography>
          </Stack>
        </Stack>
        <TableContainer sx={{ borderWidth: 0, '& .MuiTableCell-root': { padding: '12px' } }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('field', { ns: '@cibo/landmanager/SelectRollupEditor' })}</TableCell>
                <TableCell>
                  {programEngagementFields.data?.items &&
                    programEngagementFields.data.items.length > 1 && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography style={{ fontSize: '14px', width: '130px' }}>
                          {t('selectedPractices')}
                        </Typography>
                        <MultiselectChangeAllInput
                          selectProps={{
                            renderValue: value => t('bulkApply'),
                            size: 'small',
                            autoWidth: true,
                            sx: {
                              width: '130px',
                              '& .MuiInputBase-inputSizeSmall': {
                                paddingBlock: '2px',
                              },
                            },
                          }}
                          optionsRenderOverride={optionsOverride({ bulk: true })}
                        />
                      </Stack>
                    )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programEngagementFields.data?.items.map(field => (
                <TableRow key={field.resourceId}>
                  <TableCell>
                    {/* @ts-ignore this isn't a data grid, so it's missing some props */}
                    <FieldNameCell thumbnailSize={50} row={field} stackProps={{ paddingY: 0 }} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="column">
                      <MultiselectFieldInput
                        disabled={options.length === 0 || fieldAnswers[field.resourceId]?.immutable}
                        row={field}
                        renderSelectedValue={displayString(t)}
                        value={{
                          result:
                            recommendedPractices.data?.fields?.[field.resourceId]
                              .selectedPractices || [],
                          immutable: fieldAnswers[field.resourceId]?.immutable,
                        }}
                        style={{ width: 'calc(100% - 30px)' }}
                        optionsRenderOverride={optionsOverride({ bulk: false })}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Card>
  )
}

export const FieldsSelectionV2 = <T extends GeneralResourceDetail = GeneralResourceDetail>(
  props: FieldsSelectionV2Props<T>
) => {
  const recommendedPractices = useRecommendedFieldPractices(props.engagementId)

  const updateFields = useUpdateMultipleFields([
    RECOMMENDED_PRACTICES_QUERY_KEY,
    props.engagementId,
  ])

  const taskData = useEngagementTaskData({
    engagementId: props.engagementId,
    taskId: props.task.id,
  })

  if (recommendedPractices.isLoading || taskData.isLoading) {
    return <SkeletonTable columnDefs={[200, 200]} />
  }

  if (recommendedPractices.isError || taskData.isError) {
    return <NotFound />
  }

  const options =
    recommendedPractices.data?.fields &&
    uniqBy(
      (a: PracticeDefinition) => a.practice.code,
      // Combine practices across all fields
      Object.values(recommendedPractices.data.fields).flatMap(fieldRecommendation =>
        fieldRecommendation.pools.flatMap(({ practicePoints }: FundingPool) =>
          // Take up to 10 practices from each pool
          practicePoints.slice(0, 10)
        )
      ) || []
    ).map(practice => ({ label: practice.practice.label, value: practice.practice.code }))

  return (
    <MultiselectCheckboxProvider
      engagementId={props.engagementId}
      optionsList={options}
      traitId={props.detailRequirements[0]?.traitId || ''}
      proprietaryValueSource={{
        data: recommendedPractices,
        retriever: (data, fieldId) => {
          return data.data?.fields[fieldId]?.selectedPractices || []
        },
      }}
      handleChange={updateFields.mutateAsync}
      onError={props.onError}
      onSuccess={props.onSuccess}
      onUpdating={props.onUpdating}
      year={props.detailRequirements[0]?.year}
    >
      <FieldsSelectionV2Display {...props} />
    </MultiselectCheckboxProvider>
  )
}
